import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../cx';
import { Icon } from './Icon';

/** Marca escrita com o ponto amarelo: "MODO.", "BIS.". O ponto é o único amarelo da tela. */
export function Wordmark({ children, size = 22, className }: { children: string; size?: number; className?: string }) {
  return <span className={cx('rr-wordmark', className)} style={{ fontSize: size }}>{children}<span className="rr-wordmark__dot" aria-hidden="true" /></span>;
}

/** Um anexo no campo de conversa: enquanto a Machine lê, `reading`; não lido, `failed` com o motivo em `note`. */
export interface AskFile { id: string; name: string; state: 'sending' | 'reading' | 'ready' | 'failed'; note?: string | null; thumbUrl?: string | null; kind?: 'image' | 'pdf' | 'sheet' | 'file' }
const FILE_ICON = { image: 'image', pdf: 'file', sheet: 'table', file: 'file' } as const;

/**
 * O campo de conversa: texto que cresce, anexos, ditado e enviar. Enter envia, Shift+Enter quebra a linha.
 * É o componente central do MODO ("o campo é o produto"). Anexo: `attachment` (um só, só o nome) ou `files` (vários, cada
 * um com a situação da leitura). Ditado: com `onAudio`, aparece o microfone; a gravação fica no aparelho e quem a
 * transcreve é quem recebe o áudio (o servidor): nunca o reconhecimento de fala do navegador.
 */
export function AskField({ value, onChange, onSubmit, placeholder, disabled, attachment, onAttach, onClearAttachment, autoFocus, accept, files, onRemoveFile, multiple, onAudio, transcribing, onAudioError, tools }: {
  value: string; onChange: (v: string) => void; onSubmit: () => void; placeholder?: string; disabled?: boolean;
  /** controles a mais na barra, ao lado do anexo (um modo do pedido, por exemplo) */
  tools?: ReactNode;
  attachment?: string | null; onAttach?: (file: File) => void; onClearAttachment?: () => void; autoFocus?: boolean; accept?: string;
  files?: AskFile[]; onRemoveFile?: (id: string) => void; multiple?: boolean;
  onAudio?: (audio: Blob) => void; transcribing?: boolean; onAudioError?: (message: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => { const el = ref.current; if (!el) return; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 220)}px`; }, [value]);
  useEffect(() => { if (!recording) return; setSeconds(0); const t = window.setInterval(() => setSeconds((x) => x + 1), 1000); return () => window.clearInterval(t); }, [recording]);
  useEffect(() => () => { rec.current?.stream.getTracks().forEach((t) => t.stop()); }, []);
  const busyFiles = (files ?? []).some((f) => f.state === 'sending');
  const can = !disabled && !recording && !transcribing && !busyFiles && (value.trim().length > 0 || Boolean(attachment) || (files ?? []).length > 0);
  const voice = Boolean(onAudio) && typeof MediaRecorder !== 'undefined';
  const record = async () => {
    if (recording) { rec.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream), chunks: Blob[] = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); setRecording(false); const blob = new Blob(chunks, { type: r.mimeType || 'audio/webm' }); if (blob.size > 0) onAudio?.(blob); };
      rec.current = r; r.start(); setRecording(true);
    } catch { onAudioError?.('não consegui usar o microfone: confira a permissão do navegador'); }
  };
  return (
    <div className={cx('rr-askfield', recording && 'is-recording')}>
      {files && files.length > 0 && (
        <div className="rr-askfield__files">
          {files.map((f) => (
            <span key={f.id} className={cx('rr-askfield__chip', `is-${f.state}`)} title={f.note ?? f.name}>
              {f.thumbUrl ? <img src={f.thumbUrl} alt="" /> : <span className="rr-askfield__chipicon"><Icon name={FILE_ICON[f.kind ?? 'file']} size={14} /></span>}
              <span className="rr-askfield__chipname"><span>{f.name}</span><small>{f.state === 'sending' ? 'enviando' : f.state === 'reading' ? 'lendo' : f.state === 'failed' ? (f.note ?? 'não deu para ler') : 'lido'}</small></span>
              {onRemoveFile && <button type="button" aria-label={`tirar ${f.name}`} onClick={() => onRemoveFile(f.id)}><Icon name="x" size={12} /></button>}
            </span>
          ))}
        </div>
      )}
      {recording
        ? <div className="rr-askfield__rec" aria-live="polite"><span className="rr-askfield__recdot" />gravando · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</div>
        : <textarea ref={ref} className="rr-askfield__input" rows={1} value={value} placeholder={transcribing ? 'transcrevendo o que você disse…' : placeholder} autoFocus={autoFocus} aria-label={placeholder ?? 'mensagem'} disabled={transcribing}
            onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (can) onSubmit(); } }} />}
      <div className="rr-askfield__bar">
        <div className="rr-askfield__tools">
          {onAttach && !recording && (
            <>
              <button type="button" className="rr-askfield__tool" aria-label="anexar um arquivo" title="anexar imagem, planilha ou PDF" disabled={disabled} onClick={() => file.current?.click()}><Icon name={files ? 'paperclip' : 'plus'} size={16} /></button>
              <input ref={file} type="file" hidden accept={accept} multiple={multiple} onChange={(e) => { for (const f of Array.from(e.target.files ?? [])) onAttach(f); e.target.value = ''; }} />
            </>
          )}
          {!recording && tools}
          {attachment && <span className="rr-askfield__file"><span>{attachment}</span>{onClearAttachment && <button type="button" aria-label="tirar o anexo" onClick={onClearAttachment}><Icon name="x" size={12} /></button>}</span>}
        </div>
        <div className="rr-askfield__tools">
          {voice && <button type="button" className={cx('rr-askfield__tool', recording && 'is-on')} aria-label={recording ? 'parar e transcrever' : 'falar em vez de digitar'} title={recording ? 'parar e transcrever' : 'falar em vez de digitar'} disabled={disabled || transcribing} onClick={() => void record()}><Icon name={recording ? 'stop' : 'mic'} size={16} /></button>}
          <button type="button" className="rr-askfield__send" disabled={!can} aria-label="enviar" onClick={onSubmit}><Icon name="arrow-right" size={16} /></button>
        </div>
      </div>
    </div>
  );
}

/** Um pedido pronto, abaixo do campo. Poucos: quatro, no máximo. */
export function Suggestion({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return <button type="button" className="rr-suggest" onClick={onClick}>{children}</button>;
}

/** Uma fala da conversa. A da pessoa vai à direita, num balão; a do sistema ocupa a largura e pode trazer qualquer coisa. */
export function Turn({ who, children }: { who: 'me' | 'system'; children: ReactNode }) {
  return <div className={cx('rr-turn', who === 'me' && 'rr-turn--me')}>{typeof children === 'string' ? <p className="rr-turn__text">{children}</p> : children}</div>;
}

/**
 * Esqueleto de carregamento: a forma do que vai chegar, com um brilho passando. Usar no lugar de "carregando…" quando se sabe
 * o formato da resposta (uma busca, um cartão, uma peça). `ratio` para caixas com proporção (uma imagem); `lines` para texto.
 */
export function Skeleton({ width, height, ratio, lines, radius, className }: { width?: number | string; height?: number | string; ratio?: number; lines?: number; radius?: number | string; className?: string }) {
  if (lines) return <span className={cx('rr-skeleton-lines', className)} style={{ width }} aria-hidden="true">{Array.from({ length: lines }, (_, i) => <span key={i} className="rr-skeleton" style={{ width: i === lines - 1 && lines > 1 ? '62%' : '100%' }} />)}</span>;
  return <span className={cx('rr-skeleton', className)} style={{ width, height, aspectRatio: ratio, borderRadius: radius }} aria-hidden="true" />;
}

/**
 * O que a Machine está fazendo enquanto a resposta não chega: os passos reais, na ordem em que acontecem, e embaixo o esqueleto
 * da resposta. `stage` é um passo; `item` é uma coisa que ela está lendo. O último passo fica pulsando. Nunca inventar passo.
 */
export function Thinking({ label, steps, seconds, lines = 4 }: { label: string; steps: { kind: 'stage' | 'item'; text: string }[]; seconds?: number; lines?: number }) {
  const lastStage = steps.map((s) => s.kind).lastIndexOf('stage');
  return (
    <div className="rr-thinking" role="status" aria-live="polite">
      <div className="rr-thinking__head">
        <span className="rr-label rr-label--accent"><span className="rr-live__pulse" />{label}</span>
        {seconds !== undefined && seconds > 0 && <span className="rr-thinking__time">{seconds}s</span>}
      </div>
      <div className="rr-live">
        {steps.map((s, i) => (
          <div key={`${s.kind}-${s.text}`} className={cx('rr-live__row', s.kind === 'stage' ? 'rr-live__row--stage' : 'rr-thinking__item', i === lastStage && 'rr-thinking__now')} style={s.kind === 'item' ? { animationDelay: `${Math.min(i, 14) * 70}ms` } : undefined}>
            <span>{s.text}</span>
          </div>
        ))}
      </div>
      <Skeleton lines={lines} />
    </div>
  );
}

/** Texto que se escreve sozinho, com o cursor piscando no fim enquanto escreve. `onDone` avisa quando terminou. */
export function Typed({ text, speed = 14, onDone, caret = true }: { text: string; speed?: number; onDone?: () => void; caret?: boolean }) {
  const el = useRef<HTMLSpanElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const node = el.current;
    if (!node) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { node.textContent = text; node.dataset.done = '1'; done.current?.(); return; }
    let i = 0;
    node.textContent = '';
    delete node.dataset.done;
    const timer = window.setInterval(() => {
      i = Math.min(text.length, i + Math.max(1, Math.round(text.length / 90)));
      node.textContent = text.slice(0, i);
      if (i >= text.length) { window.clearInterval(timer); node.dataset.done = '1'; done.current?.(); }
    }, speed);
    return () => window.clearInterval(timer);
  }, [text, speed]);
  return <span ref={el} className={cx('rr-typed', caret && 'rr-typed--caret')} />;
}
